import { React, useState, useEffect } from 'react';
import { Spinner, Alert, ProjectService } from '../../services';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-tooltip/dist/react-tooltip.css';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useDispatch } from 'react-redux';
import { saveValue } from '../../reducers/Voucher';
import MarkdownEditorComponent from './MarkdownEditor';
import ConfirmationPop from '../../components/ConfirmationPop';
import moment from 'moment';
import {
  CrossIcon,
  ImageIcon,
  UploadIcon,
} from '../../components/icons/IconProvider';
import { Switch } from '@headlessui/react';
import FormInput from '../../components/FormInput';

const AddEditProject = () => {
  const parseApiDateToDate = (value) => {
    if (!value) return null;
    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : null;
  };

  const normalizeDateForApi = (value) => {
    if (value instanceof Date) {
      return moment(value).format('YYYY-MM-DD');
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    return '';
  };

  const [categoryList, setCategoryList] = useState();
  const [documents, setDocuments] = useState([]);
  const [projectImgs, setProjectImgs] = useState([]);
  const [description, setDescription] = useState('');
  const [dutchDescription, setDutchDescription] = useState('');
  const [symbolExistError, setSymbolExistError] = useState(false);
  const [isProjectType, setProjectType] = useState('');
  const [projectDetails, setProjectDetails] = useState(null);
  const navigate = useNavigate();
  const [isProjectCost, setProjectCost] = useState(null);
  const [removedDocIds, setRemovedDocIds] = useState([]);
  const [removedImgIds, setRemovedImgIds] = useState([]);
  const [isExpectedApy, setExpectedApy] = useState(null);
  const [isInriscScore, setInriscScore] = useState(null);
  const [isInriscScoreLink, setInriscScoreLink] = useState(null);
  const [enabled, setEnabled] = useState(false);

  const dispatch = useDispatch();
  const fileSize = 2048;
  const [previewImage, setPreviewImage] = useState({
    image: null,
    source: null,
  });
  //confirmation modal
  const [isCommonConfirmationPop, setCommonConfirmationPop] = useState(false);
  const [isConfirmationPopData, setConfirmationPopData] = useState({
    FirstText: '',
    SecondText: '',
    FirstButton: '',
    secondButton: '',
    slug: '',
  });

  const supportedFormats = ['image/jpg', 'image/jpeg', 'image/png'];
  let params = useParams();
  // Check if mode is 'edit' and an id is provided
  let projectId = params ? params.id : null;

  let validationShape = {
    investment_type: Yup.string().required('Investment type is required'),
    name: Yup.string()
      .required('Project name is required.')
      .min(6, 'Minimum 6 characters are required.')
      .max(100, 'Maximum length of the name is 100 characters.')
      .nullable(),
    bond_issue_date: Yup.mixed().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Bond issue date is required');
        }
      },
    ),
    maturity_date: Yup.mixed().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Maturity date is required');
        }
      },
    ),
    esignature_template_id_en: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Agreement template id is required');
        }
      },
    ),
    esignature_template_id_nl: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Dutch template id is required');
        }
      },
    ),
    interestRate: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema
            .required('Interest rate is required')
            .matches(/^[1-9]\d*(?:\.\d+)?$/, 'Only digits are allowed.')
            .test('fileSize', 'Should not be more than 100%.', (value) => {
              if (value > 100) {
                return false;
              } else {
                return true;
              }
            });
        }
      },
    ),
    agreement_term_url: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Agreement term url is required');
        }
      },
    ),
    payout_cycle: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Payout cycle is required');
        }
      },
    ),
    min_purchase_qty: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Minimum purchase quantity is required');
        }
      },
    ),
    schedule_type: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Schedule Type is required');
        }
      },
    ),
    inrisk_frame_url: Yup.string()
      .min('6', 'Minimum 6 characters are required.')
      .max('500', 'Should not be greater than 500 characters.')
      .matches(/^(http|https):\/\/[^ "]+$/, 'Enter correct url')
      .nullable(),
    Symbol: Yup.string()
      .required('Symbol is required')
      .min(4, 'Minimum 4 alphanumeric characters required'),
    location: Yup.string()
      .required('Location is required.')
      .min(6, 'Minimum 6 characters are required.')
      .max(100, 'Location should not be more than 100 characters.')
      .nullable(),
    projectType: Yup.string().required('Project type is required.').nullable(),

    energyCapacity: Yup.string()
      .when('investment_type', (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          schema
            .matches(/^[0-9]\d*(?:\.\d+)?$/, 'Only digits are allowed.')
            .nullable();
          return schema;
        } else {
          return schema
            .matches(/^[1-9]\d*(?:\.\d+)?$/, 'Only digits are allowed.')
            .required('Energy capacity is required.');
        }
      })
      .max(10, 'Energy capacity should not be more than 10 digit.')
      .nullable(),
    available_for_sale: Yup.number()
      .typeError('Available for sale is required.')
      .test(
        'fileSize',
        'Available for sale should be greater than 0.',
        (value) => {
          if (value < 0) {
            return false;
          } else {
            return true;
          }
        },
      )
      .max(
        Yup.ref('totalQty'),
        'Available for sale should not be greater than minted quantity.',
      ),

    totalQty: Yup.number()
      .typeError('Tokens to be minted is required.')
      .required('Tokens to be minted is required.')
      .test(
        'fileSize',
        'Tokens to be minted should be greater than 0.',
        (value) => {
          if (value <= 0) {
            return false;
          } else {
            return true;
          }
        },
      )
      .nullable(),
    price: Yup.string()
      .required('Price is required.')
      .max(20, 'Price should not greater than 20 digit.')
      .matches(
        /^[0-9]*(\.[0-9]+)?$/,
        'Please enter the price using only numerical digits.',
      )
      .test('fileSize', 'Price should be greater than 0.', (value) => {
        if (value <= 0.0) {
          return false;
        } else {
          return true;
        }
      })
      .nullable(),
    pricePerNFT: Yup.string()
      .required('Nominal value is required.')
      .max(20, 'Nominal value should not greater than 20 digit.')
      .matches(
        /^[0-9]*(\.[0-9]+)?$/,
        'Please enter the price for your token using only numerical digits.',
      )
      .test('fileSize', 'Nominal value should be greater than 0.', (value) => {
        if (value <= 0.0) {
          return false;
        } else {
          return true;
        }
      })
      .nullable(),

    //
    issuer_name: Yup.string().when(
      'investment_type',
      (investment_type, schema) => {
        if (investment_type && investment_type[0] === 'bond') {
          return schema.required('Issuer name is required');
        }
      },
    ),
    lifeSpanTo: Yup.mixed()
      .required('Project end date is required.')
      .nullable(),
    lifeSpanFrom: Yup.mixed()
      .required('Project start date is required.')
      .nullable(),
  };

  // primary image validation

  validationShape['projectPrimaryImage'] = Yup.mixed();

  if (previewImage?.image == null && previewImage?.source == null) {
    validationShape['projectPrimaryImage'] = validationShape[
      'projectPrimaryImage'
    ]
      .test(
        'required',
        'Project primary image is required',
        (value) => value.length > 0,
      )
      .test(
        'fileFormat',
        'Project primary image format unsupported',
        (value) =>
          value === null ||
          (value && supportedFormats.includes(value[0]?.type)),
      );
  }

  validationShape['projectPrimaryImage'] = validationShape[
    'projectPrimaryImage'
  ].test('fileSize', 'Project primary image size is too large', (value) => {
    if (value && value.length > 0) {
      return Math.round(value[0].size / 1024 <= fileSize);
    } else {
      return true;
    }
  });

  if (isProjectCost) {
    validationShape['projectCost'] = Yup.string()
      .optional()
      .max(20, 'Asset value should not be greater 20 digit.')
      .matches(/^[1-9]\d*(?:\.\d+)?$/, 'Only digits are allowed.')
      .nullable();
  }

  if (isExpectedApy) {
    validationShape['expectedApy'] = Yup.string()
      .matches(/^[1-9]\d*(?:\.\d+)?$/, 'Only digits are allowed.')
      .test('fileSize', 'Should not be more than 100%.', (value) => {
        if (value > 100) {
          return false;
        } else {
          return true;
        }
      })

      .nullable();
  }
  if (isInriscScore) {
    validationShape['inriscScore'] = Yup.string()
      .matches(/^[1-9]\d*(?:\.\d+)?$/, 'Only digits are allowed.')
      .test('fileSize', 'InRisc score should be between 1 - 100.', (value) => {
        if (value > 100) {
          return false;
        } else {
          return true;
        }
      })
      .nullable();
  }
  if (isInriscScoreLink) {
    validationShape['inriscScoreLink'] = Yup.string()
      .min('6', 'Minimum 6 characters are required.')
      .max('500', 'Should not be greater than 500 characters.')
      .matches(/^(http|https):\/\/[^ "]+$/, 'Enter correct url')
      .nullable();
  }

  const validationSchema = Yup.object().shape(validationShape).required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      investment_type: 'asset',
      professional_investors_only: 0,
    },
  });

  useEffect(() => {
    (async () => {
      if (projectId) {
        await getProjectDetails();
      }
      await getCategoryList();
    })();
  }, []);

  const getCategoryList = async () => {
    try {
      let response = await ProjectService.getCategory();

      if (response.status === 'failure') {
        return false;
      }

      setCategoryList(response.data.records);
    } catch (error) {
      Alert.error(error.message);
      return false;
    }
  };

  const checkSymbol = async (e) => {
    try {
      if (e.target.value.length < 4) {
        return false;
      }
      const formData = {
        Symbol: e.target.value,
      };
      let response = await ProjectService.checkSymbol(formData);

      if (response.status === 'failure') {
        setSymbolExistError(true);

        return false;
      } else {
        setSymbolExistError(false);
      }
    } catch (error) {
      setSymbolExistError(true);
      Alert.error(error.message);
      return false;
    }
  };

  const changeHandler = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return false;
    }
    let documentsLength = documents.length + e.target.files.length;
    if (documentsLength > 15) {
      Alert.error('Please upload no more than 15 documents');
      return false;
    }

    const DocArr = [];

    [...e.target.files].forEach((file) => {
      if (!file.name.match(/\.(pdf)$/)) {
        Alert.error('Please select valid document.');
        return false;
      } else if (Math.round(file.size / 1024) > 10000) {
        Alert.error('The document file size should not exceed 10MB.');
        return false;
      } else {
        DocArr.push({
          data: file,
          url: URL.createObjectURL(file),
          name: file.name || 'example.pdf',
        });
      }
    });
    e.target.value = null;
    setDocuments([...documents, ...DocArr]);
  };

  const onSubmit = async (_formInput) => {
    Spinner.show();
    let response = { status: 'failure' };

    if (!projectId && previewImage.source === null) {
      Spinner.hide();
      return false;
    }

    if (
      projectId &&
      previewImage.source === null &&
      previewImage.image === null
    ) {
      Spinner.hide();
      return false;
    }

    try {
      let formData = new FormData();
      formData.append('investment_type', _formInput.investment_type);
      formData.append('name', _formInput.name);
      formData.append('symbol', _formInput.Symbol);
      formData.append('location', _formInput.location);
      formData.append('project_type_id', _formInput.projectType);
      formData.append('energy_capacity', _formInput.energyCapacity);
      formData.append('project_cost', _formInput.projectCost);
      formData.append('total_minted', _formInput.totalQty);
      formData.append('price', _formInput.price);

      //set state
      dispatch(saveValue(_formInput.totalQty));

      formData.append('nominal_value', _formInput.pricePerNFT);
      formData.append(
        'life_span_from',
        normalizeDateForApi(_formInput.lifeSpanFrom),
      );
      formData.append(
        'life_span_to',
        normalizeDateForApi(_formInput.lifeSpanTo),
      );
      formData.append('available_for_sale', _formInput.available_for_sale);

      formData.append('inrisc_score_link', _formInput.inriscScoreLink);
      formData.append('inrisc_score', _formInput.inriscScore);
      formData.append('inrisk_frame_url', _formInput.inrisk_frame_url);

      formData.append('expected_apy', _formInput.expectedApy);

      if (_formInput.investment_type === 'bond') {
        formData.append('payout_cycle', _formInput.payout_cycle);
        formData.append('schedule_type', _formInput.schedule_type);
        formData.append(
          'bond_issue_date',
          normalizeDateForApi(_formInput.bond_issue_date),
        );
        formData.append(
          'maturity_date',
          normalizeDateForApi(_formInput.maturity_date),
        );
        formData.append('min_purchase_qty', _formInput.min_purchase_qty);
        formData.append('issuer_name', _formInput.issuer_name);
        formData.append('agreement_term_url', _formInput.agreement_term_url);
        formData.append('interest_rate', _formInput.interestRate);
        formData.append(
          'esignature_template_id_en',
          _formInput.esignature_template_id_en,
        );
        formData.append(
          'esignature_template_id_nl',
          _formInput.esignature_template_id_nl,
        );
        formData.append('isin', _formInput.isin);
      }
      formData.append('professional_investors_only', enabled ? 1 : 0);
      formData.append('description', description);
      formData.append('description_dutch', dutchDescription);
      formData.append('projectImage', previewImage.source);
      if (documents && Object.keys(documents).length > 0) {
        for (let i = 0; i < documents.length; i++) {
          if (documents[i].data !== null && documents[i].data instanceof File) {
            formData.append('project_document[]', documents[i].data);
          }
        }
      }
      if (removedImgIds.length > 0) {
        formData.append('remove_images', removedImgIds);
      }
      if (removedDocIds.length > 0) {
        formData.append('remove_document', removedDocIds);
      }
      if (projectImgs && Object.keys(projectImgs).length > 0) {
        let gameImgs = projectImgs.filter(
          (gameImgSnap) => gameImgSnap.data != null && gameImgSnap.data !== '',
        );

        if (gameImgs && gameImgs.length) {
          const filteredData = gameImgs.filter((item) =>
            item.url.startsWith('blob:'),
          );
          filteredData.map((img, index) => {
            formData.append(`project_images[${index}]`, img.data);
          });
        }
      }
      if (projectId) {
        response = await ProjectService.updateProject(projectId, formData);
      } else {
        response = await ProjectService.createProject(formData);
      }
      if (response.status === 'failure') {
        Spinner.hide();
        Alert.error(response.message);
        return false;
      }

      Spinner.hide();
      Alert.success(response.message);
      // navigate("/projects/list");
      if (projectId) {
        navigate(`/assets/view/${projectId}`);
      } else {
        navigate('/assets/view/' + response?.data?.id);
      }
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
      return false;
    }
  };
  const handleImageUpload = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return false;
    }

    let gameImgLength = projectImgs.length + e.target.files.length;
    if (gameImgLength > 6) {
      Alert.error('A maximum of six additional images can be uploaded');
      return false;
    }

    if (!e.target.files[0].name.match(/\.(jpg|jpeg|png|gif)$/i)) {
      Alert.error(
        `Please select valid extension image. ${e.target.files[0].name}`,
      );
      return false;
    }

    const imageSize = Math.round(e.target.files[0].size / 1024);
    if (imageSize >= fileSize) {
      Alert.error('The image file size should not exceed 2 MB.');
      return false;
    }

    const ImgArr = [];

    [...e.target.files].forEach((file) => {
      ImgArr.push({
        data: file,
        url: URL.createObjectURL(file),
      });
    });

    // Clear the file input value to allow selecting the same file again
    e.target.value = null;

    setProjectImgs([...projectImgs, ...ImgArr]);
  };

  const deleteImage = (_index) => {
    let filteredGame = projectImgs.filter((game, i) => i !== _index);
    setProjectImgs(filteredGame);
  };
  const deleteDocument = (_index) => {
    let filteredGame = documents.filter((game, i) => i !== _index);

    setDocuments(filteredGame);
  };

  const {
    onChange: onProjectPrimaryImageChange,
    ...projectPrimaryImageHandlerProps
  } = register('projectPrimaryImage');

  const handlelogoUpload = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setPreviewImage({
        ...previewImage,
        image: null,
        source: null,
      });
      return false;
    }

    if (!e.target.files[0].name.match(/\.(jpg|jpeg|png)$/i)) {
      setPreviewImage({
        ...previewImage,
        image: null,
        source: null,
      });
      Alert.error(
        `Please select valid extension image. ${e.target.files[0].name}`,
      );
      return false;
    }

    if (Math.round(e.target.files[0].size / 1024) > fileSize) {
      setPreviewImage({
        ...previewImage,
        image: null,
        source: null,
      });
      Alert.error('The image file size should not exceed 2 MB.');
      return false;
    }

    if (e.target.files.width >= 600) {
      setPreviewImage({
        ...previewImage,
        image: null,
        source: null,
      });
      return false;
    }

    const objectUrl = URL.createObjectURL(e.target.files[0]);

    setPreviewImage({
      ...previewImage,
      image: objectUrl,
      source: e.target.files[0],
    });
  };

  const removePrimaryImage = () => {
    setValue('projectPrimaryImage', '');
    setPreviewImage({
      image: null,
      source: null,
    });
  };

  const cancelProject = async () => {
    setConfirmationPopData({
      FirstText: 'Warning',
      SecondText: 'Do you want to cancel?',
      FirstButton: 'No',
      secondButton: 'Yes',
      slug: 'redirect',
    });
    setCommonConfirmationPop(true);

    return false;
  };

  const confirmationPopHandel = async (slug) => {
    if (slug === 'redirect') {
      setCommonConfirmationPop(false);
      navigate('/assets');
      return false;
    }
  };

  // Update project
  const getProjectDetails = async () => {
    Spinner.show();
    try {
      let response = await ProjectService.getProjectDetails(projectId);
      if (response.status === 'failure') {
        Spinner.hide();
        Alert.error(response.message);
        return false;
      }

      Spinner.hide();

      const savedValues = {
        investment_type: response.data.investment_type
          ? response.data.investment_type
          : '',
        name: response.data.name ? response.data.name : '',
        Symbol: response.data.symbol ? response.data.symbol : '',
        location: response.data.location ? response.data.location : '',
        energyCapacity: response.data.energy_capacity
          ? response.data.energy_capacity
          : '',
        price: response.data.price
          ? Number(response.data.price).toFixed(2)
          : '0',
        available_for_sale: response.data.available_for_sale
          ? response.data.available_for_sale
          : '0',
        projectCost: response.data.project_cost
          ? Number(response.data?.project_cost).toFixed(2)
          : '',
        totalQty: response.data.total_minted ? response.data.total_minted : '',
        pricePerNFT: response.data.nominal_value
          ? Number(response.data?.nominal_value).toFixed(2)
          : '',
        expectedApy: response.data.expected_apy
          ? response.data.expected_apy
          : '',
        inriscScore: response.data.inrisc_score
          ? response.data.inrisc_score
          : '',
        inriscScoreLink: response.data.inrisc_score_link
          ? response.data.inrisc_score_link
          : '',
        description: response.data.description
          ? setDescription(response.data.description)
          : '',
        description_dutch: response.data.description_dutch
          ? setDutchDescription(response.data.description_dutch)
          : '',
        lifeSpanFrom: parseApiDateToDate(response.data.life_span_from),
        lifeSpanTo: parseApiDateToDate(response.data.life_span_to),
        projectType: response.data.projectsType?.id
          ? response.data.projectsType?.id
          : '',
        esignature_template_id_en: response.data.esignature_template_id_en
          ? response.data.esignature_template_id_en
          : '',
        payout_cycle: response.data.payout_cycle
          ? response.data.payout_cycle
          : '',
        schedule_type: response.data.schedule_type
          ? response.data.schedule_type
          : '',
        isin: response.data.isin ? response.data.isin : '',
        inrisk_frame_url: response.data.inrisk_frame_url
          ? response.data.inrisk_frame_url
          : '',
        bond_issue_date: parseApiDateToDate(response.data.bond_issue_date),
        maturity_date: parseApiDateToDate(response.data.maturity_date),
        min_purchase_qty: response.data.min_purchase_qty
          ? response.data.min_purchase_qty
          : '',
        issuer_name: response.data.issuer_name ? response.data.issuer_name : '',
        agreement_term_url: response.data.agreement_term_url
          ? response.data.agreement_term_url
          : '',
        esignature_template_id_nl: response.data.esignature_template_id_nl
          ? response.data.esignature_template_id_nl
          : '',
        interestRate: response.data.interest_rate
          ? response.data.interest_rate
          : '',
        professional_investors_only: response.data.professional_investors_only
          ? response.data.professional_investors_only
          : 0,
      };

      setProjectType(response.data.projectsType?.id);

      const gameImgArr = [];
      [...response.data.projectImages].forEach((file) => {
        if (file?.type === 'image') {
          gameImgArr.push({
            data: file.id,
            url: file.resourceUrl,
          });

          setProjectImgs(gameImgArr);
        }
      });
      let DocArr = [];
      [...response.data.projectDocument].forEach((file) => {
        DocArr.push({
          data: file,
          url: file.resourceUrl,
          name: file.name || 'example.pdf',
        });
        setDocuments(DocArr);
      });

      setPreviewImage({
        image: response.data.project_image,
        source: null,
      });
      reset(savedValues);
      setEnabled(response.data.professional_investors_only);
      setProjectDetails(response.data);
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
      return false;
    }
  };

  // Remove images
  const deleteUpdatedImage = (_index, Id) => {
    let filteredGame = projectImgs.filter((game, i) => i !== _index);
    if (Id) {
      let _removedImgs = removedImgIds;
      _removedImgs.push(Id);
      setRemovedImgIds(_removedImgs);
    }

    setProjectImgs(filteredGame.reverse());
  };

  //Remove Documents
  const deleteUpdatedDocument = (_index, id) => {
    let filteredGame = documents.filter((game, i) => i !== _index);
    if (id) {
      let _removedImgs = removedDocIds;
      _removedImgs.push(id);
      setRemovedDocIds(_removedImgs);
    }
    setDocuments(filteredGame);
  };
  return (
    <div className="">
      <div className="w-full mb-4">
        <h4 className=""> {projectId ? 'Edit Project' : 'Add New Project'}</h4>
      </div>
      <div className="md:px-6 md:py-6 p-4 bg-brandGray rounded-xl">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid md:grid-cols-3 gap-5">
            <div>
              <PrimaryImageUpload
                previewImage={previewImage}
                errors={errors}
                onProjectPrimaryImageChange={onProjectPrimaryImageChange}
                handlelogoUpload={handlelogoUpload}
                removePrimaryImage={removePrimaryImage}
                projectPrimaryImageHandlerProps={
                  projectPrimaryImageHandlerProps
                }
              />
              <AdditionalImagesUpload
                projectImgs={projectImgs}
                projectId={projectId}
                handleImageUpload={handleImageUpload}
                deleteImage={deleteImage}
                deleteUpdatedImage={deleteUpdatedImage}
              />
            </div>
            <div className="md:col-span-2">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <p className="text-sm md:text-md mb-1">
                    Investement Type
                    <span className="common-error-text">*</span>
                  </p>
                  <div className="bg-white p-2 rounded-lg inline-block">
                    <button
                      type="button"
                      className={`text-sm font-medium rounded-md px-4 p-2 w-20 ${
                        watch('investment_type') === 'asset'
                          ? 'bg-brandBlue text-white'
                          : 'bg-white text-brandBlue '
                      }`}
                      onClick={() => setValue('investment_type', 'asset')}
                      disabled={
                        projectDetails?.contract_address != null ? true : false
                      }
                    >
                      Equity
                    </button>
                    <button
                      type="button"
                      className={`text-sm font-medium rounded-md px-4 p-2 w-20 ${
                        watch('investment_type') === 'bond'
                          ? 'bg-brandBlue text-white'
                          : 'bg-white text-brandBlue '
                      }`}
                      onClick={() => setValue('investment_type', 'bond')}
                      disabled={
                        projectDetails?.contract_address != null ? true : false
                      }
                    >
                      Bond
                    </button>
                  </div>
                  <div className="common-error-text">
                    {errors.investment_type?.message}
                  </div>
                </div>
                <div className="">
                  <FormInput
                    label="Project Name"
                    name="name"
                    control={control}
                    register={register}
                    errors={errors}
                    placeholder="Project Name"
                    required={true}
                    className=""
                  />
                </div>
                {watch('investment_type') === 'bond' && (
                  <>
                    <div className="">
                      <FormInput
                        label="Issuer Name"
                        name="issuer_name"
                        control={control}
                        register={register}
                        errors={errors}
                        placeholder="Issuer Name"
                        required={true}
                        className=""
                      />
                    </div>
                  </>
                )}
                <div className="">
                  <FormInput
                    label="Symbol"
                    name="Symbol"
                    register={register}
                    placeholder="ABLX0000"
                    type="text"
                    required={true}
                    onInput={(e) => checkSymbol(e)}
                    id="Symbol"
                  />
                  {symbolExistError === false && (
                    <div className="common-error-text">
                      {errors.Symbol?.message}
                    </div>
                  )}
                  {symbolExistError && (
                    <span className="common-error-text">
                      Symbol already exists
                    </span>
                  )}
                </div>
                <div className="">
                  <FormInput
                    label="Project Location"
                    name="location"
                    register={register}
                    errors={errors}
                    placeholder="Amst, Netherlands"
                    type="text"
                    required={true}
                    id="location"
                  />
                </div>
                <div className="">
                  <FormInput
                    label="Project Type"
                    name="projectType"
                    register={register}
                    errors={errors}
                    required={true}
                    type="select"
                    options={categoryList?.map((category) => ({
                      value: category.id,
                      label: category.name,
                    }))}
                    onChange={(e) => setProjectType(e.target.value)}
                    value={isProjectType}
                    className="h-12"
                  />
                </div>
                <div className="">
                  <FormInput
                    label="Peak Production Capacity"
                    name="energyCapacity"
                    register={register}
                    errors={errors}
                    required={true}
                    type="text"
                    placeholder="Energy Capacity"
                    maxLength={10}
                    id="energyCapacity"
                    className="relative"
                    append={
                      <span className="absolute top-1/2 -translate-y-1/2 right-4">
                        MW
                      </span>
                    }
                  />
                </div>
                <div className="">
                  <FormInput
                    label="Asset Value (€)"
                    name="projectCost"
                    register={register}
                    type="text"
                    placeholder="Asset Value (€)"
                    maxLength={10}
                    onInput={(e) => setProjectCost(e.target.value)} // Handle input as before
                    className="w-full"
                    id="projectCost"
                  />

                  {isProjectCost && (
                    <div className="common-error-text">
                      {errors.projectCost?.message}
                    </div>
                  )}
                </div>
                <div className="">
                  <FormInput
                    label="Tokens to be Minted"
                    name="totalQty"
                    register={register}
                    errors={errors}
                    required={true}
                    type="number"
                    placeholder="Tokens to be Minted"
                    maxLength={20}
                    className="appearance-none"
                  />
                </div>
                <div className="">
                  <FormInput
                    label="Price (€)"
                    name="price"
                    register={register}
                    errors={errors}
                    required={true}
                    type="text"
                    placeholder="Price (€)"
                    className="appearance-none"
                  />
                </div>
                <div className="">
                  <FormInput
                    label="Available For Sale"
                    name="available_for_sale"
                    register={register}
                    errors={errors}
                    required={true}
                    type="number"
                    placeholder="Available For Sale"
                    className="appearance-none"
                  />
                </div>
                {/* For Bond */}

                {watch('investment_type') === 'bond' && (
                  <FormInput
                    type="date-picker"
                    label="Bond Issue Date"
                    name="bond_issue_date"
                    control={control}
                    errors={errors}
                    required
                    placeholder="Bond Issue Date"
                    onChange={(date) => {
                      setValue('bond_issue_date', date?.startDate ?? null, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  />
                )}

                {watch('investment_type') === 'bond' && (
                  <>
                    <FormInput
                      type="date-picker"
                      label="Maturity Date"
                      name="maturity_date"
                      control={control}
                      errors={errors}
                      required
                      placeholder="Maturity Date"
                      minDate={
                        watch('bond_issue_date')
                          ? watch('bond_issue_date')
                          : null
                      }
                      onChange={(date) => {
                        setValue('maturity_date', date?.startDate ?? null, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                    />

                    <div>
                      <FormInput
                        label="Minimum Purchase Quantity"
                        name="min_purchase_qty"
                        register={register}
                        required={true}
                        errors={errors}
                        type="text"
                        placeholder="0"
                        className="appearance-none"
                      />
                    </div>
                    <div className="">
                      <FormInput
                        label="Agreement Term URL"
                        name="agreement_term_url"
                        register={register}
                        errors={errors}
                        required={true}
                        type="text"
                        placeholder="Agreement Term URL"
                        className="appearance-none"
                      />
                    </div>
                  </>
                )}
                {/* For Bond */}
                <div className="">
                  <FormInput
                    label="Nominal value (€)"
                    name="pricePerNFT"
                    register={register}
                    errors={errors}
                    required={true}
                    type="text"
                    placeholder="Nominal value (€)"
                    className="appearance-none"
                  />
                </div>
                {watch('investment_type') === 'bond' && (
                  <div className="">
                    <FormInput
                      label="ISIN"
                      name="isin"
                      register={register}
                      errors={errors}
                      type="text"
                      placeholder="ISIN"
                      className="appearance-none"
                    />
                  </div>
                )}
                {watch('investment_type') === 'bond' && (
                  <div className="">
                    <p className="text-sm md:text-md mb-1">
                      {watch('investment_type') === 'bond' ? (
                        <>
                          Interest Rate
                          <span className="text-red-500">*</span>{' '}
                        </>
                      ) : (
                        'Expected APY'
                      )}
                    </p>
                    <div className="relative">
                      <FormInput
                        name={
                          watch('investment_type') === 'bond'
                            ? 'interestRate'
                            : 'expectedApy'
                        }
                        register={register}
                        errors={errors}
                        required={true}
                        type="number"
                        placeholder={
                          watch('investment_type') === 'bond'
                            ? 'Interest Rate'
                            : 'Expected APY'
                        }
                        step={0.01}
                        onInput={
                          watch('investment_type') === 'asset'
                            ? (e) => setExpectedApy(e.target.value)
                            : undefined
                        }
                        append={
                          <span className="absolute top-1/2 -translate-y-1/2 right-4">
                            %
                          </span>
                        }
                        className="appearance-none"
                      />
                    </div>
                  </div>
                )}
                <div className="">
                  <FormInput
                    label="Expected APY"
                    name="expectedApy"
                    register={register}
                    errors={errors}
                    type="number"
                    placeholder="Expected APY"
                    step={0.01}
                    onInput={(e) => setExpectedApy(e.target.value)}
                    append={
                      <span className="absolute top-1/2 -translate-y-1/2 right-4">
                        %
                      </span>
                    }
                    className="appearance-none"
                  />
                </div>

                {/* // Project Start Date */}
                <div className="datePickInput">
                  <FormInput
                    type="date-picker"
                    label=" Project Start Date"
                    name="lifeSpanFrom"
                    control={control}
                    errors={errors}
                    required
                    placeholder="Project Start Date"
                    onChange={(date) => {
                      setValue('lifeSpanFrom', date?.startDate ?? null, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  />
                </div>

                {/* //Project End Date */}
                <div className="datePickInput">
                  <FormInput
                    type="date-picker"
                    label=" Project End Date"
                    name="lifeSpanTo"
                    control={control}
                    errors={errors}
                    required
                    placeholder="Project end date"
                    minDate={
                      watch('lifeSpanFrom') ? watch('lifeSpanFrom') : null
                    }
                    onChange={(date) => {
                      setValue('lifeSpanTo', date?.startDate ?? null, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  />
                </div>
                <div className="">
                  <FormInput
                    label="InRisc Score"
                    name="inriscScore"
                    register={register}
                    errors={errors}
                    type="text"
                    placeholder="InRisc Score"
                    onInput={(e) => setInriscScore(e.target.value)}
                    className="appearance-none"
                  />
                </div>
                <div className="">
                  <FormInput
                    label="InRisc Score Link"
                    name="inriscScoreLink"
                    register={register}
                    errors={errors}
                    type="text"
                    placeholder="InRisc Score Link"
                    onInput={(e) => setInriscScoreLink(e.target.value)} // Handle input changes
                    className="appearance-none"
                  />
                </div>
                {/* For Bond */}
                <div className="">
                  <FormInput
                    label="InRisc Graph iFrame"
                    name="inrisk_frame_url"
                    register={register}
                    errors={errors}
                    required={true}
                    type="text"
                    placeholder="iFrame Code"
                    className="appearance-none"
                  />
                </div>
                <div className="">
                  <p className="text-sm md:text-md mb-3">
                    Professional Investors
                  </p>
                  <Switch
                    checked={enabled}
                    onChange={setEnabled}
                    className={`${enabled ? 'bg-GreenColor' : 'bg-gray-400'} p-0 relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                  >
                    <span
                      aria-hidden="true"
                      className={`${enabled ? 'translate-x-7' : 'translate-x-0'} pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                    />
                  </Switch>
                </div>
                {watch('investment_type') === 'bond' && (
                  <div className="">
                    <FormInput
                      label="Interest Payout Cycle"
                      name="payout_cycle"
                      register={register}
                      errors={errors}
                      required={true}
                      type="select"
                      options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'yearly', label: 'Yearly' },
                      ]}
                      className="h-12"
                    />
                  </div>
                )}
                {watch('investment_type') === 'bond' && (
                  <div className="">
                    <FormInput
                      label="Schedule Type"
                      name="schedule_type"
                      register={register}
                      errors={errors}
                      required={true}
                      type="select"
                      options={[
                        { value: 'every_period', label: 'Every Period' },
                        { value: 'end_of_period', label: 'End of Period' },
                      ]}
                      className="h-12"
                    />
                  </div>
                )}
                {watch('investment_type') === 'bond' && (
                  <>
                    <div className="">
                      <FormInput
                        label="eSignature Template ID (EN)"
                        name="esignature_template_id_en"
                        register={register}
                        errors={errors}
                        required={true}
                        type="text"
                        placeholder="uuid"
                      />
                    </div>
                    <div className="">
                      <FormInput
                        label="eSignature Template ID (NL)"
                        name="esignature_template_id_nl"
                        register={register}
                        errors={errors}
                        required={true}
                        type="text"
                        placeholder="uuid"
                      />
                    </div>
                  </>
                )}
                <div className="md:col-span-2">
                  <MarkdownEditorComponent
                    description={description}
                    setDescription={setDescription}
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="font-semibold p-2">Dutch Description</p>
                  <MarkdownEditorComponent
                    description={dutchDescription}
                    setDescription={setDutchDescription}
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm md:text-md mb-5">
                    Upload Document
                    <span className="text-xs md:ml-2 md:mt-0 mt-2 bg-[#fff9e4] text-[#897530] p-3 rounded-md md:inline-block block">
                      (Only .PDF format accept. upto 10 MB)
                    </span>
                  </p>
                  <div className="md:flex items-center">
                    <div className="md:mr-5 md:mb-0 mb-8">
                      <label className="relative inline-block py-2 px-4 rounded-lg bg-brandBlue text-white">
                        <div className="flex items-center text-sm">
                          <UploadIcon />
                          Browse Documents
                        </div>
                        <input
                          type="file"
                          name="documents"
                          id="dropzone-file"
                          accept="pdf/*"
                          multiple={true}
                          onChange={changeHandler}
                          className="absolute w-full h-full top-0 left-0 opacity-0"
                        />
                      </label>
                    </div>

                    <div className="flex flex-wrap  items-start gap-4">
                      {documents &&
                        documents.map((data, index) => {
                          return (
                            <div
                              key={index}
                              className="w-20  rounded-lg relative bg-white p-2  items-center justify-center mr-4"
                            >
                              <Link target="_blank" to={data.url}>
                                <img
                                  src="/img/PDF-Icon.png"
                                  className="mx-auto mb-2 w-8"
                                  alt=""
                                />
                              </Link>
                              <span
                                onClick={() =>
                                  projectId
                                    ? deleteUpdatedDocument(index, data.data.id)
                                    : deleteDocument(index)
                                }
                                className="bg-white absolute -top-3 -right-3 shadow-sm inline-flex p-1 items-center justify-center rounded-full w-6 h-6 text-red-500 cursor-pointer"
                              >
                                <CrossIcon />
                              </span>
                              <Link target="_blank" to={data.url}>
                                <p
                                  data-tooltip-id={`pdf-name-${index}`}
                                  className="text-xs whitespace-nowrap overflow-ellipsis overflow-hidden mt-2 w-16"
                                >
                                  {data.name}
                                </p>
                                <ReactTooltip
                                  id={`pdf-name-${index}`}
                                  place="bottom"
                                  content={data?.name}
                                />
                              </Link>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end mt-6">
            <button
              type="button"
              onClick={() => cancelProject()}
              className="bg-transparent border border-brandBlue text-brandBlue mr-3 text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={symbolExistError ? true : false}
              className="border bg-brandBlue border-brandBlue text-white mr-3 text-sm font-semibold"
            >
              {projectId ? 'Update' : ' Save as Draft'}
            </button>
          </div>
        </form>
      </div>
      <ConfirmationPop
        isPop={isCommonConfirmationPop}
        setPop={setCommonConfirmationPop}
        functionCall={confirmationPopHandel}
        FirstText={isConfirmationPopData.FirstText}
        secondText={isConfirmationPopData.SecondText}
        FirstButton={isConfirmationPopData.FirstButton}
        secondButton={isConfirmationPopData.secondButton}
        slug={isConfirmationPopData.slug}
      />
    </div>
  );
};

export const PrimaryImageUpload = ({
  previewImage,
  onProjectPrimaryImageChange,
  handlelogoUpload,
  removePrimaryImage,
  projectPrimaryImageHandlerProps,
  errors,
}) => {
  return (
    <>
      <p className="text-md font-medium mb-1">
        Project Primary Image <span className="common-error-text">*</span>
      </p>

      <div className="h-48 border border-solid  bg-white mb-5  border-gray-300 rounded-xl flex items-center justify-center relative">
        <div className="text-center text-gray-500">
          <ImageIcon />
          <p className="text-base">Upload Project Primary Image</p>
        </div>
        <input
          className="absolute top-0 left-0 w-full h-full opacity-0  cursor-pointer"
          type="file"
          id="projectPrimaryImage"
          name="projectPrimaryImage"
          accept=".jpg, .jpeg, .png"
          {...projectPrimaryImageHandlerProps}
          onChange={(e) => {
            onProjectPrimaryImageChange(e);
            handlelogoUpload(e);
          }}
        />
        {previewImage != null && previewImage.image != null && (
          <div>
            <img
              alt=""
              src={previewImage.image || '/img/project-image.png'}
              className="absolute top-0 left-0 w-full h-full object-cover rounded-xl cursor-pointer"
            />
            <span
              onClick={() => removePrimaryImage()}
              className="bg-white absolute -top-3 -right-3 shadow-sm inline-flex p-1 items-center justify-center rounded-full w-6 h-6 text-red-500 cursor-pointer"
            >
              <CrossIcon />
            </span>
          </div>
        )}
      </div>
      <div className="common-error-text mb-5">
        {errors?.projectPrimaryImage && errors?.projectPrimaryImage?.message}
      </div>

      <div className="bg-[#fff9e4] text-[#897530] p-3 rounded-md md:text-sm text-xs text-left mb-5">
        <span className="font-semibold">Note:</span> File types supported: JPG,
        JPEG, PNG.
        <br /> Max upload size: 2 MB
      </div>
    </>
  );
};

export const AdditionalImagesUpload = ({
  projectImgs,
  projectId,
  handleImageUpload,
  deleteImage,
  deleteUpdatedImage,
}) => {
  return (
    <>
      <div className="md:mr-5 md:mb-0 mb-4 md:mt-12 mt-4 border-t border-gray-300 md:pt-12 pt-4">
        <p className="text-md font-medium mb-1">Project Additional Images</p>

        <label className="relative inline-block py-2 px-4 rounded-lg bg-brandBlue text-white">
          <div className="flex items-center text-sm">
            <UploadIcon />
            Upload
          </div>
          <input
            type="file"
            multiple={true}
            onChange={handleImageUpload}
            className="absolute w-full h-full top-0 left-0 opacity-0"
          />
        </label>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-5 ">
        {projectImgs &&
          projectImgs.map((picture, index) => {
            return (
              <div key={index} className="w-full relative rounded-md">
                <div className="h-24 overflow-hidden rounded-md bg-white">
                  <img
                    alt="Project img"
                    src={picture.url || '/img/project-image.png'}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span
                  onClick={() =>
                    projectId
                      ? deleteUpdatedImage(index, picture?.data)
                      : deleteImage(index)
                  }
                  className="bg-white absolute -top-3 -right-3 shadow-sm inline-flex p-1 items-center justify-center rounded-full w-6 h-6 text-red-500 cursor-pointer"
                >
                  <CrossIcon />
                </span>
              </div>
            );
          })}
      </div>
    </>
  );
};
export default AddEditProject;
